import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getWebApp } from '@/lib/telegram';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle2, XCircle, Clock, ArrowDownCircle, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { StarIcon } from '@/components/ui/star-icon';
import { intentLabel as labelForIntent, sourceLabel } from '@/lib/providers';
import { formatNumber, shortDateTime } from '@/lib/date';
import { formatMinor } from '@/lib/currency';
import { Skeleton, SkeletonCard, SkeletonLine } from '@/components/ui/skeleton';
import { Group, Row } from '@/components/ui/tiles';

export const Route = createFileRoute('/payments')({
  component: PaymentsHistory,
});

function StatusBadge({ status, incoming }: { status: string; incoming: boolean }) {
  if (status === 'refunded') return <ArrowDownCircle className="w-5 h-5 text-foreground" />;
  if (status === 'pending') return <Clock className="w-5 h-5 text-[var(--t-warn)]" />;
  // Приход и расход различаем стрелкой, а не одной галочкой на всё: в объединённой
  // ленте рядом стоят «купил» и «начислили», и одинаковый значок их сливает.
  if (status === 'paid') {
    return incoming ? (
      <ArrowDownCircle className="w-5 h-5 text-[var(--t-ok)]" />
    ) : (
      <CheckCircle2 className="w-5 h-5 text-[var(--t-ok)]" />
    );
  }
  return <XCircle className="w-5 h-5 text-[var(--t-danger)]" />;
}

const STATUS_COLOR: Record<string, string> = {
  paid: 'text-[var(--t-ok)]',
  pending: 'text-[var(--t-warn)]',
  refunded: 'text-foreground',
};

/** Ключи подписей статуса счёта. Незнакомый статус показываем как «не прошёл». */
const STATUS_LABEL: Record<string, string> = {
  paid: 'Payment status paid',
  pending: 'Payment status pending',
  failed: 'Payment status failed',
  cancelled: 'Payment status cancelled',
  expired: 'Payment status expired',
  refunded: 'Payment status refunded',
};

function PaymentsHistory() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['payments-history'],
    queryFn: () => api.paymentsHistory(),
  });

  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.BackButton) return;
    tg.BackButton.show();
    const onBack = () => navigate({ to: '/wallet' });
    tg.BackButton.onClick(onBack);
    return () => tg.BackButton.offClick(onBack);
  }, [navigate]);

  useEffect(() => {
    const tg = getWebApp();
    tg?.MainButton.hide();
  }, []);

  const items = data?.items ?? [];

  return (
    <div className="p-4 space-y-3 pb-24">
      <h2 className="tl-h text-[32px] font-semibold leading-tight tracking-[-0.035em] px-2 pt-2 text-foreground">{t('Payments history')}</h2>
      {isPending ? (
        <PaymentsSkeleton />
      ) : isError ? (
        <Card>
          <CardContent className="p-6 text-center space-y-3 flex flex-col items-center">
            <AlertTriangle className="w-7 h-7 text-[var(--t-danger)]" />
            <div className="text-sm text-[var(--t-muted)]">
              {(error as Error)?.message || t('Payments load failed')}
            </div>
            <button
              type="button"
              onClick={() => refetch()}
              className="calm-cta px-4 py-2 rounded-[var(--r-btn)] text-xs font-bold"
            >
              {t('Retry')}
            </button>
          </CardContent>
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-[var(--t-muted)]">
            {t('Payments empty')}
          </CardContent>
        </Card>
      ) : (
        <Group>
          {items.map((p) => {
            const intentLabel = labelForIntent(p.intent_type, p.signed, p.source);
            // XTR (Telegram Stars) — целые звёзды, без деления на 100. Рубли с копейками
            // до двух знаков: «349,50 ₽», а не «349,5 ₽».
            const amount =
              p.currency === 'XTR' ? (
                <span className="inline-flex items-center gap-0.5 whitespace-nowrap">
                  {formatNumber(p.amount_display_minor, i18n.language)}
                  <StarIcon className="h-3.5 w-3.5 text-[#f5b400]" aria-label="⭐" />
                </span>
              ) : (
                formatMinor(p.amount_display_minor, p.currency, i18n.language)
              );
            const succeeded = p.status === 'paid' || p.status === 'refunded';
            const incoming = p.signed > 0;
            return (
              <Row
                key={p.id}
                icon={<StatusBadge status={p.status} incoming={incoming} />}
                // Подпись переносится, а не режется: «Возврат заявки на вывод» и
                // «Перенос баланса при объединении…» иначе обрывались на полуслове.
                title={<span className="[overflow-wrap:anywhere]">{intentLabel}</span>}
                hint={`${sourceLabel(p.source, p.provider)} · ${shortDateTime(p.created_at, i18n.language)}`}
                right={
                  <span className="shrink-0 text-right">
                    {/* Знак обязателен: начисление бонуса без плюса читается как списание. */}
                    <span className={cn('block whitespace-nowrap text-[15px] font-medium tabular', incoming && succeeded ? 'text-[var(--t-ok)]' : 'text-foreground')}>
                      {incoming ? '+' : '−'}
                      {amount}
                    </span>
                    {/* У операции по балансу статуса не бывает: проводка либо есть, либо
                        её нет. Подпись «оплачено» на каждой строке была бы шумом, поэтому
                        словом показываем только то, что пошло не так или ещё ждёт. */}
                    {p.source === 'payment' && p.status !== 'paid' && (
                      <span className={cn('mt-0.5 block whitespace-nowrap text-[12px]', STATUS_COLOR[p.status] ?? 'text-[var(--t-faint)]')}>
                        {t(STATUS_LABEL[p.status] ?? 'Payment status failed')}
                      </span>
                    )}
                  </span>
                }
              />
            );
          })}
        </Group>
      )}
    </div>
  );
}

/** Повторяет список платежей: значок статуса, назначение с датой, сумма. */
function PaymentsSkeleton() {
  return (
    <SkeletonCard className="min-h-0 divide-y divide-[var(--t-line)] rounded-[var(--r-card)]">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex min-h-[62px] items-center gap-3.5 px-4 py-3">
          <Skeleton className="size-9 shrink-0 rounded-[var(--r-sm)]" />
          <div className="min-w-0 flex-1 space-y-2">
            <SkeletonLine className="w-[45%]" />
            <SkeletonLine size="xs" className="w-[65%]" />
          </div>
          <SkeletonLine className="w-14 shrink-0" />
        </div>
      ))}
    </SkeletonCard>
  );
}
